import { useEffect, useState } from 'react';
import { Spinner } from 'shared/src/components';
import { phoneNumberFormatter } from 'shared/src/utils';
import { getScheduledCoworkers } from '../fetchers';
import { renderDataAttribute } from './WorkSchedules';

type ScheduledCoworkersListProps = {
  workScheduleId: string;
};

type Coworker = {
  Address?: string;
  Age?: string;
  CellPhone: string;
  CityStateZip?: string;
  Email: string;
  Name: string;
  PollworkerId?: string;
  WorkerEndTime: string;
  WorkerRole: string;
  WorkerStartTime: string;
};

export default function ScheduledCoworkersList({ workScheduleId }: ScheduledCoworkersListProps) {
  const [loading, setLoading] = useState(true);
  const [scheduledCoworkers, setScheduledCoworkers] = useState<Coworker[]>([]);

  useEffect(() => {
    if (!workScheduleId) return;

    setLoading(true);

    getScheduledCoworkers(workScheduleId)
      .then((response) => {
        setScheduledCoworkers(response);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [workScheduleId]);

  if (loading) {
    return (
      <div className="min-h-56 relative">
        <div className="flex items-center justify-center space-x-3 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <Spinner show />
          <div>Loading coworkers...</div>
        </div>
      </div>
    );
  }

  if (!scheduledCoworkers.length) {
    return <div className="min-h-56 flex flex-col justify-center w-full relative text-sm text-center text-gray-700">No scheduled coworkers were found.</div>;
  }

  return (
    <div className="max-h-80 w-0 min-w-full overflow-auto">
      <table className="w-full min-w-[36rem] table-fixed divide-y divide-gray-300 text-left text-sm">
        <thead>
          <tr>
            <th scope="col" className="w-[27%] px-3 py-3.5 text-sm font-semibold text-gray-900">
              Coworker
            </th>
            <th scope="col" className="w-[23%] px-3 py-3.5 text-sm font-semibold text-gray-900">
              Role
            </th>
            <th scope="col" className="w-[30%] px-3 py-3.5 text-sm font-semibold text-gray-900">
              Contact
            </th>
            <th scope="col" className="w-[20%] px-3 py-3.5 text-sm font-semibold text-gray-900">
              Shift
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {scheduledCoworkers.map((coworker, idx) => {
            const phoneNumber = phoneNumberFormatter({ value: coworker.CellPhone });

            return (
              <tr key={`${coworker.PollworkerId}_${coworker.Name}_${idx}`}>
                <td className="px-3 py-4 align-top font-medium text-gray-900">
                  <div className="truncate" title={coworker.Name}>
                    {renderDataAttribute(coworker.Name)}
                  </div>
                </td>
                <td className="px-3 py-4 align-top text-gray-700">
                  <div className="truncate" title={coworker.WorkerRole}>
                    {renderDataAttribute(coworker.WorkerRole)}
                  </div>
                </td>
                <td className="px-3 py-4 align-top text-gray-700">
                  <div className="space-y-0.5">
                    <div className="truncate" title={coworker.Email || undefined}>
                      {renderDataAttribute(coworker.Email)}
                    </div>
                    <div className="truncate" title={phoneNumber || undefined}>
                      {renderDataAttribute(phoneNumber)}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-4 align-top tabular-nums text-gray-700">
                  <div className="whitespace-nowrap">{renderDataAttribute(coworker.WorkerStartTime)}</div>
                  <div className="whitespace-nowrap">to {renderDataAttribute(coworker.WorkerEndTime)}</div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
